import { useEffect, useState } from 'react';
import { agentApi, type AgentStats } from '@/shared/api/agentApi';

/**
 * "הביצועים שלי" — המדדים של הנציג עצמו, לצד לשונית הפעולות.
 *
 * הנתונים מגיעים מ-`GET /agent/stats` שכבר קיים ומשמש גם את מסך הסופטפון,
 * ולכן אין כאן חישוב בצד הלקוח: מספר שמוצג לנציג חייב להיות אותו מספר
 * שהמנהל רואה, אחרת שני המסכים חלוקים על אותה עובדה.
 */

const WEEKDAYS = ['א׳', 'ב׳', 'ג׳', 'ד׳', 'ה׳', 'ו׳', 'ש׳'];

function fmtDuration(sec?: number): string {
  if (!sec || sec <= 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function Metric({ label, value }: { label: string; value: string | number }) {
  return (
    <div style={{
      flex: 1, minWidth: 90, padding: '8px 10px',
      background: 'var(--surface-2)', borderRadius: 'var(--r-2)',
    }}>
      <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{label}</div>
      <div style={{ fontSize: 'var(--t-lg)', fontWeight: 700 }}>{value}</div>
    </div>
  );
}

export function AgentPerformance() {
  const [stats, setStats] = useState<AgentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    agentApi.stats()
      .then(setStats)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>טוען…</div>;
  if (error) return <div style={{ color: 'var(--danger)', fontSize: 'var(--t-sm)' }}>שגיאה בטעינת המדדים.</div>;

  const today = stats?.today;
  const chart = stats?.week_chart ?? [];
  const peak = Math.max(1, ...chart.map((d) => d.count));

  return (
    <div className="stack" style={{ gap: 12 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Metric label="שיחות היום" value={today?.calls ?? 0} />
        <Metric label="נענו" value={today?.answered ?? 0} />
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Metric label="זמן שיחה" value={fmtDuration(today?.total_talk_sec)} />
        <Metric label="ממוצע לשיחה" value={fmtDuration(today?.aht_sec)} />
      </div>

      {chart.length > 0 && (
        <div>
          <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginBottom: 6 }}>
            השבוע האחרון
          </div>
          <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 64 }}>
            {chart.map((d) => (
              <div key={d.day} style={{ flex: 1, textAlign: 'center' }}>
                <div
                  title={`${d.day}: ${d.count}`}
                  style={{
                    height: Math.max(3, (d.count / peak) * 46),
                    background: d.count > 0 ? 'var(--accent)' : 'var(--line)',
                    borderRadius: 'var(--r-1, 3px)',
                  }}
                />
                <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: 3 }}>
                  {WEEKDAYS[new Date(d.day).getDay()]}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(stats?.badges?.length ?? 0) > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {stats!.badges!.map((b) => (
            <span
              key={b.code}
              style={{
                padding: '3px 10px', borderRadius: 'var(--r-pill)',
                background: 'var(--accent-soft)', color: 'var(--accent-ink)',
                fontSize: 'var(--t-xs)', fontWeight: 600,
              }}
            >
              {b.label_he}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
